import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import { wrenchModeSchedulesRestCrudThunksForSupplier } from '../../thunks/wrench_mode_schedules_thunks';
import {
	WrenchHubUserAnalytic,
	fetchWrenchHubUserAnalyticsForSupplier,
} from '../../thunks/wrench_mode_events_thunks';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import WrenchModeLogsHeader from './WrenchModeLogsHeader';
import WrenchModeLogsSidebar from './WrenchModeLogsSidebar';
import WrenchModeLogsTable from './WrenchModeLogsTable';

require('./FieldTechsOverviewWrenchModeLogsPage.less');

const SCHEDULES_TARGET_COLLECTION = 'overviewWrenchModeSchedules';
const PAGE_SIZE = 10;
const SCHEDULES_SORTING = { sort_by: 'date', order: 'descend' };

interface Filters {
	supplierEmail?: string;
	locationIds?: number[];
}

const monthRange = (d: moment.Moment) => ({
	fromDate: d.clone().startOf('month').format('YYYY-MM-DD'),
	toDate: d.clone().endOf('month').format('YYYY-MM-DD'),
});

const buildScheduleParams = (date: moment.Moment, filters: Filters) => {
	const { fromDate, toDate } = monthRange(date);
	const params: Record<string, any> = { fromDate, toDate };
	if (filters.supplierEmail) {
		params.supplierEmail = filters.supplierEmail;
	}
	return params;
};

const parseInitialState = (search: string) => {
	const params = new URLSearchParams(search);
	const date = params.get('date');
	const supplierEmail = params.get('supplierEmail') || undefined;
	const locationIdsParam = params.get('locationIds');
	const locationIds = locationIdsParam
		? locationIdsParam.split(',').map(Number).filter(Boolean)
		: undefined;

	return {
		date: date ? moment(date) : moment(),
		filters: {
			supplierEmail,
			locationIds: locationIds && locationIds.length > 0 ? locationIds : undefined,
		} as Filters,
	};
};

const FieldTechsOverviewWrenchModeLogsPage = (props: any) => {
	const {
		fetchSchedulesThunk,
		schedules,
		schedulesLoading,
		schedulesPagination,
		schedulesCount,
		fieldTechsState,
		fetchFieldTechs,
		fetchMultipleFieldTechs,
		history,
	} = props;

	const initial = parseInitialState(history.location.search);
	const [selectedDate, setSelectedDate] = useState(initial.date);
	const [filters, setFilters] = useState<Filters>(initial.filters);

	const [techAnalytics, setTechAnalytics] = useState<WrenchHubUserAnalytic[]>([]);
	const [techAnalyticsLoading, setTechAnalyticsLoading] = useState(false);

	// Sync state to query params so the URL is shareable / back-navigable
	useEffect(() => {
		const params = new URLSearchParams();
		params.set('date', selectedDate.format('YYYY-MM-DD'));
		if (filters.supplierEmail) {
			params.set('supplierEmail', filters.supplierEmail);
		}
		if (filters.locationIds && filters.locationIds.length > 0) {
			params.set('locationIds', filters.locationIds.join(','));
		}
		const newSearch = params.toString();
		if (history.location.search !== '?' + newSearch) {
			history.replace({ pathname: history.location.pathname, search: newSearch });
		}
	}, [selectedDate, filters, history]);

	const monthKey = selectedDate.format('YYYY-MM');
	const filtersKey = filters.supplierEmail || '';
	const latestAnalyticsRequest = useRef<string>('');

	const fetchSchedulesForMonth = useCallback(
		(date: moment.Moment, activeFilters: Filters) => {
			const params = buildScheduleParams(date, activeFilters);
			return fetchSchedulesThunk(
				params,
				SCHEDULES_TARGET_COLLECTION,
				{ current: 1, pageSize: PAGE_SIZE },
				SCHEDULES_SORTING,
				null,
				false
			).catch(() => {
				/* errors tracked in redux via fetchError */
			});
		},
		[fetchSchedulesThunk]
	);

	const fetchAnalyticsForMonth = useCallback((date: moment.Moment) => {
		const { fromDate, toDate } = monthRange(date);
		const requestKey = `${fromDate}/${toDate}`;
		latestAnalyticsRequest.current = requestKey;
		setTechAnalyticsLoading(true);

		fetchWrenchHubUserAnalyticsForSupplier(fromDate, toDate)
			.then((data) => {
				if (latestAnalyticsRequest.current !== requestKey) return;
				setTechAnalytics(data);
				setTechAnalyticsLoading(false);
			})
			.catch(() => {
				if (latestAnalyticsRequest.current !== requestKey) return;
				setTechAnalytics([]);
				setTechAnalyticsLoading(false);
			});
	}, []);

	// Initial load + refetch when month or filters change
	useEffect(() => {
		fetchSchedulesForMonth(selectedDate, filters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [monthKey, filtersKey, fetchSchedulesForMonth]);

	useEffect(() => {
		fetchAnalyticsForMonth(selectedDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [monthKey, fetchAnalyticsForMonth]);

	const handleDateChange = useCallback((newDate: moment.Moment) => {
		setSelectedDate(newDate);
	}, []);

	const handleFilterChange = useCallback(
		(newFilters: Filters) => {
			setFilters({ ...filters, ...newFilters });
		},
		[filters]
	);

	const handleLoadMore = useCallback(() => {
		const currentPage = nullSafeGetOrElse('current', schedulesPagination, 1);
		const params = buildScheduleParams(selectedDate, filters);
		fetchSchedulesThunk(
			params,
			SCHEDULES_TARGET_COLLECTION,
			{ current: currentPage + 1, pageSize: PAGE_SIZE },
			SCHEDULES_SORTING,
			null,
			true
		).catch(() => {
			/* errors tracked in redux via fetchError */
		});
	}, [schedulesPagination, selectedDate, filters, fetchSchedulesThunk]);

	const loadedCount = schedules.length;
	const hasMore = loadedCount > 0 && loadedCount < schedulesCount;
	const loadingMore = schedulesLoading && loadedCount > 0;
	const initialLoading = schedulesLoading && loadedCount === 0;

	return (
		<div className="wrenchModeLogsPage mx-xs flex flex-col gap-s">
			<WrenchModeLogsHeader
				filters={filters}
				onFilterChange={handleFilterChange}
				fieldTechsState={fieldTechsState}
				fetchFieldTechs={fetchFieldTechs}
				fetchMultipleFieldTechs={fetchMultipleFieldTechs}
			/>
			<div className="flex gap-s">
				<WrenchModeLogsSidebar
					selectedDate={selectedDate}
					onDateChange={handleDateChange}
					techAnalytics={techAnalytics}
					loading={techAnalyticsLoading}
				/>
				<div className="flex-1 overflow-auto">
					<WrenchModeLogsTable
						schedules={schedules}
						loading={initialLoading}
						loadingMore={loadingMore}
						hasMore={hasMore}
						onLoadMore={handleLoadMore}
						filters={filters}
					/>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state: any) => {
	const schedulesState = state.wrench_mode_schedules || {};
	const coll = schedulesState[SCHEDULES_TARGET_COLLECTION];
	const ids: number[] = nullSafeGetOrElse('recordsIds', coll, []);
	const records = nullSafeGetOrElse('records', schedulesState, {});
	const schedulesList = ids.map((id) => records[id]).filter(Boolean);

	return {
		schedules: schedulesList,
		schedulesLoading: nullSafeGetOrElse('fetching', coll, false),
		schedulesPagination: nullSafeGetOrElse('pagination', coll, {
			current: 1,
			pageSize: PAGE_SIZE,
		}),
		schedulesCount: nullSafeGetOrElse('count', coll, 0),
		fieldTechsState: state.field_techs,
	};
};

const mapDispatchToProps = (dispatch: any) => ({
	fetchSchedulesThunk: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			wrenchModeSchedulesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchFieldTechs: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleFieldTechs: (ids: string[], targetCollectionName?: string) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(FieldTechsOverviewWrenchModeLogsPage)
);
